import { useEffect, useRef } from 'react';

const STEPS = [
  {
    number: '01',
    title: 'Discovery Consultation',
    description: 'An in-depth conversation about your vision, lifestyle, goals, and investment priorities — establishing a clear foundation for a truly tailored design experience.',
    icon: 'ri-discuss-line',
    image: 'https://public.readdy.ai/ai/img_res/2f04bd1f21151a62ac1c4e6f808b83f3.jpg',
    imageAlt: 'Design consultation meeting at Lisa Merkle Design studio',
  },
  {
    number: '02',
    title: 'Concept & Curation',
    description: 'A comprehensive design concept with curated materials, finishes, and a refined spatial plan — reviewed together in detail before a single decision is finalized.',
    icon: 'ri-draft-line',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20interior%20design%20concept%20board%20material%20samples%20marble%20swatches%20fabric%20samples%20paint%20palettes%20warm%20neutral%20tones%20elegant%20design%20studio%20aesthetic%20editorial%20photography%20artistic%20flat%20lay&width=600&height=400&seq=process2&orientation=landscape',
    imageAlt: 'Interior design material curation and concept development',
  },
  {
    number: '03',
    title: 'Procurement & Coordination',
    description: 'We leverage our trusted network of premium vendors, artisans, and specialty trades to source and coordinate all elements — ensuring quality, timing, and cohesion.',
    icon: 'ri-team-line',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20furniture%20showroom%20procurement%20Beverly%20Hills%20high%20end%20pieces%20being%20selected%20warm%20interior%20showroom%20lighting%20elegant%20upholstered%20pieces%20refined%20sophisticated%20editorial%20photography&width=600&height=400&seq=process3&orientation=landscape',
    imageAlt: 'Premium furniture and material procurement coordination',
  },
  {
    number: '04',
    title: 'Installation & Reveal',
    description: 'Precise installation by experienced craftsmen, followed by the moment you first see your transformed space — beautiful, complete, and deeply personal.',
    icon: 'ri-home-smile-line',
    image: 'https://readdy.ai/api/search-image?query=Stunning%20completed%20luxury%20interior%20room%20reveal%20Beverly%20Hills%20home%20warm%20ambient%20lighting%20perfectly%20styled%20elegant%20furnishings%20architectural%20details%20aspirational%20beautiful%20editorial%20photography&width=600&height=400&seq=process4&orientation=landscape',
    imageAlt: 'Final luxury interior installation and reveal',
  },
];

export default function ProcessSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 120);
        });
        obs.disconnect();
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#F5F0E8] py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-8">

        <div className="reveal text-center mb-16">
          <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">How We Work</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-charcoal mt-4 leading-tight">
            Our Design Process
          </h2>
          <div className="flex items-center justify-center gap-4 mt-5">
            <div className="w-10 h-px bg-stone/50" />
            <p className="text-charcoal/45 text-sm font-sans font-light">
              Four deliberate stages from vision to extraordinary reality
            </p>
            <div className="w-10 h-px bg-stone/50" />
          </div>
        </div>

        {/* Steps grid — image + content cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className={`reveal reveal-delay-${i + 1} group flex flex-col bg-white overflow-hidden cursor-default`}
            >
              {/* Step image */}
              <div className="relative h-52 overflow-hidden flex-shrink-0">
                <img
                  src={step.image}
                  alt={step.imageAlt}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-charcoal/20 group-hover:bg-charcoal/10 transition-colors duration-500" />
                {/* Step number overlay */}
                <div className="absolute top-4 left-4 bg-white/95 px-3 py-2 backdrop-blur-sm">
                  <span className="font-display text-2xl font-light text-charcoal leading-none">{step.number}</span>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center border border-stone/25 group-hover:border-stone/50 transition-colors flex-shrink-0">
                    <i className={`${step.icon} text-stone text-sm`} />
                  </div>
                  <div className="w-5 h-px bg-stone/30 group-hover:w-8 group-hover:bg-stone-dark/50 transition-all duration-500" />
                </div>
                <h3 className="font-display text-xl font-light text-charcoal mb-3 leading-tight">
                  {step.title}
                </h3>
                <p className="text-charcoal/55 text-sm leading-relaxed font-sans font-light flex-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}